/**
 * The signed groups pass — what every request carries so the server need not
 * ask its database who we are.
 *
 * `/auth/me` returns it (`api/me.ts` stores it here), `authHeaders()` sends it
 * as `X-QF-Pass` on every HTTP call, and the socket clients put it in their
 * opening frame. The server checks its signature and one Redis version; see
 * `docs/plans/auth-hot-path.md`.
 *
 * ## Not authority, and not secret from the user
 *
 * The browser can read the pass but cannot change it — the server signed it and
 * refuses one that no longer matches its signature. Nothing here decides
 * anything; `lib/entitlements.ts` is still what greys a control.
 *
 * ## When it goes stale
 *
 * An admin changes this account, the server's version moves, and the next
 * response says `X-QF-Pass-Stale: 1` (that request was answered from the
 * database instead, so it was still right). `notePassResponse` sees the header
 * and asks for a new pass **once**: rate-limited, and never when the server has
 * said it issues no passes at all — otherwise a server without a signing key
 * would mark every response stale and this would ask `/auth/me` forever.
 */

let token: string | null = null;
/** The server issued a pass last time it was asked. False: it has no key. */
let issued = false;
let expiresAt = 0;
let expiryTimer: ReturnType<typeof setTimeout> | null = null;
let lastRefresh = 0;
let refresher: (() => void) | null = null;

/** Refresh this long before the pass expires, so no request carries a dead one. */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
/** At most one stale-driven refresh per this interval. */
const MIN_REFRESH_INTERVAL_MS = 10 * 1000;

function expiryOf(value: string): number {
  try {
    const body = value.split(".")[0] ?? "";
    const json = atob(body.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = Number((JSON.parse(json) as { exp?: unknown }).exp);
    return Number.isFinite(exp) ? exp * 1000 : 0;
  } catch {
    return 0;
  }
}

/** Store what `/auth/me` returned. `null` means the server issues none. */
export function setPass(value: string | null | undefined): void {
  token = value || null;
  issued = token !== null;
  expiresAt = token ? expiryOf(token) : 0;
  if (expiryTimer !== null) clearTimeout(expiryTimer);
  expiryTimer = null;
  if (token && expiresAt > 0) {
    const wait = Math.max(expiresAt - Date.now() - REFRESH_MARGIN_MS, 0);
    expiryTimer = setTimeout(() => refresher?.(), wait);
  }
}

/** Forget it — on sign-out or an account switch. */
export function clearPass(): void {
  setPass(null);
}

/** The pass for a socket's opening frame, or `undefined`. */
export function currentPass(): string | undefined {
  if (!token) return undefined;
  if (expiresAt > 0 && Date.now() >= expiresAt) return undefined;
  return token;
}

/** `X-QF-Pass`, or nothing. */
export function passHeaders(): Record<string, string> {
  const value = currentPass();
  return value ? { "X-QF-Pass": value } : {};
}

/** Who fetches a new pass. `lib/entitlements.ts` registers its loader. */
export function onPassStale(fetchNew: () => void): void {
  refresher = fetchNew;
}

/** Look at a response for the stale signal, and refresh once if it is there. */
export function notePassResponse(response: Response): void {
  if (!issued || !refresher) return;
  if (response.headers.get("X-QF-Pass-Stale") !== "1") return;
  const now = Date.now();
  if (now - lastRefresh < MIN_REFRESH_INTERVAL_MS) return;
  lastRefresh = now;
  refresher();
}

/** Test seam. */
export function resetPass(): void {
  clearPass();
  lastRefresh = 0;
  refresher = null;
}
